import { useState } from 'react';
import type { LineageAttemptRead } from '../../types/api';
import { tryLabel } from '../history/postLabels';
import { reasonLabel } from './sendBack';

interface LineagePanelProps {
  /** `request.lineage` — every rejected attempt before this one, oldest first. */
  lineage: readonly LineageAttemptRead[];
  /** `request.attempt` — this request's own place in the lineage. */
  attempt: number;
}

/**
 * The entries that were really sent back, dropping runs that failed and were
 * retried.
 *
 * The server walks `parent_request_id`, and since a failed run can be retried
 * (`POST …/retry`) not every parent was rejected. The two are told apart by
 * `attempt`: a rejection's child is one attempt further on, a retry's child
 * keeps the failed row's attempt. So an entry whose next row (or this request)
 * has the same attempt is a failure, not a rejection, and is not listed.
 */
function rejectedAttempts(
  lineage: readonly LineageAttemptRead[],
  attempt: number,
): LineageAttemptRead[] {
  return lineage.filter((entry, index) => {
    const next = lineage[index + 1]?.attempt ?? attempt;
    return entry.attempt < next;
  });
}

/** One rejected attempt: what it said, and what sent it back. */
function LineageAttempt({ attempt }: { attempt: LineageAttemptRead }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <li className="lineage__attempt">
      <p className="lineage__head">
        <span className="lineage__number">{tryLabel(attempt.attempt)}</span>
        <span className="lineage__reason">{reasonLabel(attempt.reason)}</span>
        <span className="lineage__feedback">
          {attempt.feedback || 'No note was left when it was sent back.'}
        </span>
      </p>

      {attempt.captions.length > 0 ? (
        <>
          <ul className="lineage__captions">
            {attempt.captions.map((caption, index) => (
              <li
                className={
                  isExpanded ? 'lineage__caption' : 'lineage__caption lineage__caption--clamped'
                }
                key={`${attempt.request_id}-${index}`}
              >
                {caption}
              </li>
            ))}
          </ul>
          <button
            className="button button--ghost lineage__more"
            type="button"
            aria-expanded={isExpanded}
            onClick={() => setIsExpanded((current) => !current)}
          >
            {isExpanded ? 'Show less' : 'Show these drafts in full'}
          </button>
        </>
      ) : (
        <p className="lineage__empty">That try left no drafts.</p>
      )}
    </li>
  );
}

/**
 * What was already tried on this request, and why it was sent back.
 *
 * The worker has read this since tier 2 — every rejected ancestor's drafts and
 * the reviewer's own words go into the next attempt's prompt — and the reviewer
 * could not. That asymmetry is what produces the same complaint twice: a note
 * saying "make it punchier" is worth much less when the last two attempts were
 * both sent back for being flat and neither is on screen.
 *
 * Collapsed by default, because the draft in front of the reviewer is the point
 * of the screen and this is the file behind it. Captions are clamped to a
 * couple of lines each and expand in place; nothing here is editable, and
 * nothing here spends anything.
 */
export function LineagePanel({ lineage: chain, attempt }: LineagePanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const lineage = rejectedAttempts(chain, attempt);

  if (lineage.length === 0) return null;

  return (
    <section className="lineage">
      <button
        className="lineage__toggle"
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
      >
        <span className="lineage__toggle-label">Earlier tries</span>
        <span className="lineage__count">
          {lineage.length} sent back · {isOpen ? 'hide' : 'show'}
        </span>
      </button>

      {isOpen ? (
        <>
          <p className="lineage__lede">
            What this post has already tried, oldest first. We read these notes on the next try too,
            so a new note that repeats them adds nothing.
          </p>
          <ul className="lineage__list">
            {lineage.map((attempt) => (
              <LineageAttempt attempt={attempt} key={attempt.request_id} />
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}
